import { callFunctionOnTelemetrySinks } from './callFunctionOnTelemetrySinks';
import { QosResultData } from './sinks/types/event-schemas/IQosResultData';
import { IWarnData } from './sinks/types/event-schemas/IWarnData';
import { IPerformanceData } from './sinks/types/event-schemas/IPerformanceData';
import { ICustomData } from './sinks/types/event-schemas';
import { ITelemetryContext } from './sinks/types/ITelemetryContext';
import { ITelemetrySink } from './sinks/types/ITelemetrySink';
import { getPLTMetrics } from './sinks/utils/getPLTMetrics';
import { stringifyHelper } from './sinks/utils/stringifyHelper';
import { getErrorJsonData } from './sinks/utils/getErrorJsonData';
import { getInstanceId } from './getInstanceId';
import { getLCP, getFID, getCLS, Metric as WebVitalsMetric } from 'web-vitals';
import { TelemetrySinkFactory } from './sinks/types/TelemetrySinkFactory';
import { getJSResourcesMetrics } from './sinks/utils/getResourcesMetrics';
import { addPageUnloadEventListener, removePageUnloadEventListener } from '@microsoft/applicationinsights-core-js';

export interface ITelemetryData {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Parameters used to create the telemetry instance
 */
export interface ITelemetryParams {
  /**
   * Array that stores all the telemetry sink factories
   */
  readonly sinkFactories: TelemetrySinkFactory[];
  /**
   * App-level data
   */
  readonly data: ITelemetryData | (() => ITelemetryData);
}

/**
 * Telemetry functions that will trigger the corresponding function call of telemetry providers
 */
export interface ITelemetry {
  /**
   * Send a custom telemetry event. A custom event can be any event that doesn't belong to performance, network call, QoS, etc
   */
  readonly reportCustomEvent: (name: string, data?: ICustomData) => void;
  /*
   * Send a performance event. Typical performance data includes PLT, FCP and other web vitals
   */
  readonly reportPerformance: (name: string, data: IPerformanceData) => void;
  /**
   * Send a QoS event. QoS is about availability/SLA and load performance of a piece of UI.
   * The QosProvider component calls this method internally. The client code should wrap the component with QosProvider instead of calling this method explicitly.
   */
  readonly reportQos: (name: string, data: QosResultData) => void;
  /**
   * Send a warning event
   */
  readonly warn: (name: string, data: IWarnData) => void;
  /**
   * Send an error event
   */
  readonly error: (name: string, data: unknown) => void;
  /**
   * Called when that instance of telemetry is no longer being used (e.g. just before the app is unloaded)
   * This will clear all the telemetry sinks and remove built-in global event handler
   */
  readonly destroy: () => void;
}

/**
 * Create a telemetry instance and initialize it
 * @param params The params that includes array of providers and context
 */
export function initializeTelemetry(params: ITelemetryParams): ITelemetry {
  let telemetrySinks: ITelemetrySink[] = [];
  const instanceId = getInstanceId();
  const context = [params.data, { sessionId: instanceId }, () => ({ pathname: window.location.pathname })];

  params.sinkFactories.forEach(sinkFactory => telemetrySinks.push(sinkFactory({ getContextData })));

  function getContextData(): ITelemetryContext {
    return (context ?? []).reduce<ITelemetryContext>(
      (acc, value) => {
        if (!(value instanceof Function)) {
          return { ...acc, ...value };
        }

        try {
          return { ...acc, ...value() };
        } catch (error) {}

        return acc;
      },
      { sessionId: instanceId, namespace: '', userId: '', tenantId: '', pathname: '', env: '' }
    );
  }

  function reportWebVitals(metric: WebVitalsMetric) {
    const { name, value } = metric;
    telemetry.reportPerformance(name, {
      webVital: value
    });
  }

  // Send core web-vitals
  // Reference: https://web.dev/vitals/
  getCLS(reportWebVitals);
  getFID(reportWebVitals);
  getLCP(reportWebVitals);

  // Track resource error that will be handled by error event listener in capture phase
  const errorHandler = (errorEvent: ErrorEvent) => {
    const target = errorEvent.target;
    const isElementTarget =
      target instanceof HTMLScriptElement || target instanceof HTMLLinkElement || target instanceof HTMLImageElement;
    // Report only for resource type as windows.onerror can report non-resource error. This can prevent duplicated report.
    if (isElementTarget) {
      telemetry.error('resourceError', {
        error: getErrorJsonData(errorEvent.error),
        message: errorEvent.message,
        src: target instanceof HTMLLinkElement ? target.href : (target as HTMLScriptElement | HTMLImageElement).src
      });
    } else {
      telemetry.error('jsError', { message: errorEvent.message, stack: errorEvent.error?.stack });
    }
  };

  const onLoadHandler = () => {
    // Log the browser-perceived performance metrics
    telemetry.reportPerformance('PLT', getPLTMetrics());

    // Log the JS resources metrics
    const { internalResource, combinedExternalResource } = getJSResourcesMetrics(telemetry);
    if (combinedExternalResource.totalResourceCount) {
      telemetry.reportPerformance('external-js-resources', combinedExternalResource);
    }
    if (internalResource.resources.length) {
      telemetry.reportPerformance('internal-js-resources', internalResource);
    }
  };

  const unloadHandler = (ev: Event) => {
    // log the duration from navigation start until the page unloads
    // event type can be beforeunload, pagehide, and unload
    const eventType = ev.type;
    telemetry.reportPerformance(eventType ?? 'page-unload', {
      duration: performance.now(),
      eventType
    });
    telemetry.destroy();
  };

  const unhandledrejectionHandler = (ev: PromiseRejectionEvent) => {
    unhandledrejectionTask(ev, telemetry);
  };

  // Register global event handlers
  addPageUnloadEventListener(unloadHandler);
  window.addEventListener('load', onLoadHandler);
  window.addEventListener('error', errorHandler, true);
  window.addEventListener('unhandledrejection', unhandledrejectionHandler);

  // TODO: move the creation in a create function and add an extend method
  const telemetry: ITelemetry = {
    reportCustomEvent: (name: string, data?: ICustomData) => {
      callFunctionOnTelemetrySinks(
        'reportCustomEvent',
        [
          {
            name,
            data,
            context: getContextData()
          }
        ],
        telemetrySinks
      );
    },
    reportPerformance: (name: string, data: IPerformanceData) => {
      callFunctionOnTelemetrySinks(
        'reportPerformance',
        [
          {
            name,
            data,
            context: getContextData()
          }
        ],
        telemetrySinks
      );
    },
    reportQos: (name: string, data: QosResultData) => {
      callFunctionOnTelemetrySinks(
        'reportQos',
        [
          {
            name,
            data,
            context: getContextData()
          }
        ],
        telemetrySinks
      );
    },
    warn: (name: string, data: IWarnData) => {
      callFunctionOnTelemetrySinks(
        'warn',
        [
          {
            name,
            data,
            context: getContextData()
          }
        ],
        telemetrySinks
      );
    },
    error: (name: string, data: unknown) => {
      callFunctionOnTelemetrySinks(
        'error',
        [
          {
            name,
            data,
            context: getContextData()
          }
        ],
        telemetrySinks
      );
    },
    destroy: () => {
      callFunctionOnTelemetrySinks('destroy', [getContextData()], telemetrySinks);
      telemetrySinks = [];
      // Unregister global event handlers
      removePageUnloadEventListener(unloadHandler);
      window.removeEventListener('load', onLoadHandler);
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
    }
  };

  return telemetry;
}

export const unhandledrejectionTask = (ev: PromiseRejectionEvent, telemetry: ITelemetry): void => {
  // Stringify the reason object to prevent event drop if it contains array field
  if (ev.reason?.name === 'AxiosError') {
    telemetry.error(ev.type, stringifyHelper(ev.reason.message));
  } else {
    telemetry.error(ev.type, stringifyHelper(ev.reason));
  }
};
